import { Injectable, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, Subject, catchError, of, switchMap, tap } from 'rxjs';
import Swal from 'sweetalert2';
import { NotificacionesApiService } from './notificaciones-api.service';
import {
  CampoEnlace,
  CrearNotificacionInstitucionalRequest,
  EditarNotificacionInstitucionalRequest,
  NotificacionInstitucional,
  NotificacionesFiltros,
  NotificacionesPaginadas,
  campoDeErrorEnlace
} from '../models/notificacion-institucional.model';

export interface ErrorEnlace {
  campo: CampoEnlace;
  mensaje: string;
}

const PAGINACION_VACIA: NotificacionesPaginadas = { contenido: [], paginaActual: 1, tamanoPagina: 20, hayMas: false };
const FILTROS_INICIALES: NotificacionesFiltros = { pagina: 1, tamanoPagina: 20, incluirEliminadas: false };

// Nunca mostrarle al usuario el código HTTP crudo — mensaje descriptivo según
// el caso más común, con un genérico de respaldo. El texto puntual del
// backend (err.error?.mensaje) sigue teniendo prioridad cuando viene.
function mensajePorDefecto(status?: number): string {
  switch (status) {
    case 400: return 'Los datos ingresados no son válidos. Revisalos e intentá de nuevo.';
    case 403: return 'No tenés permiso para realizar esta acción.';
    case 404: return 'No se encontró la notificación.';
    case 409: return 'La notificación ya no se puede modificar o eliminar (puede que ya se haya enviado).';
    case 0: return 'No se pudo conectar con el servidor. Verificá tu conexión e intentá de nuevo.';
    default: return 'Ha ocurrido un error. Por favor, inténtelo nuevamente.';
  }
}

@Injectable({ providedIn: 'root' })
export class NotificacionesStore {
  private readonly api = inject(NotificacionesApiService);

  private readonly _filtros = signal<NotificacionesFiltros>(FILTROS_INICIALES);
  private readonly _paginacion = signal<NotificacionesPaginadas>(PAGINACION_VACIA);
  private readonly _cargando = signal(false);
  private readonly _guardando = signal(false);
  // 400 del backend sobre un campo del enlace — se muestra inline debajo del
  // campo (requisito funcional) en vez del Swal genérico.
  private readonly _errorEnlace = signal<ErrorEnlace | null>(null);
  private readonly refreshTrigger = new Subject<void>();

  readonly filtros = this._filtros.asReadonly();
  readonly paginacion = this._paginacion.asReadonly();
  readonly cargando = this._cargando.asReadonly();
  readonly guardando = this._guardando.asReadonly();
  readonly errorEnlace = this._errorEnlace.asReadonly();

  constructor() {
    toSignal(
      this.refreshTrigger.pipe(
        switchMap(() => {
          this._cargando.set(true);
          return this.api.listar(this._filtros()).pipe(
            tap(res => { this._paginacion.set(res); this._cargando.set(false); }),
            catchError(err => {
              this._cargando.set(false);
              this._paginacion.set(PAGINACION_VACIA);
              this.mostrarError(err);
              return of(null);
            })
          );
        })
      ),
      { initialValue: null }
    );
  }

  cargar(): void { this.refreshTrigger.next(); }

  reiniciar(): void {
    this._filtros.set(FILTROS_INICIALES);
    this.cargar();
  }

  setFiltros(filtros: Partial<Omit<NotificacionesFiltros, 'pagina' | 'tamanoPagina'>>): void {
    this._filtros.update(f => ({ ...f, ...filtros, pagina: 1 }));
    this.cargar();
  }

  cambiarPagina(pagina: number): void {
    this._filtros.update(f => ({ ...f, pagina }));
    this.cargar();
  }

  cambiarTamanoPagina(tamanoPagina: number): void {
    this._filtros.update(f => ({ ...f, tamanoPagina: +tamanoPagina, pagina: 1 }));
    this.cargar();
  }

  // Usado por NotificacionNuevaComponent para hidratar el form en modo
  // editar/consulta (ver docs/specs/abmc-notificacion-institucional.md §3.3,
  // corrección #5). Solo envuelve la llamada del api service con el mismo
  // manejo de error que el resto del store (Swal.fire solo acá, nunca en el
  // componente) — no duplica la llamada HTTP.
  obtener(id: number): Observable<NotificacionInstitucional | null> {
    return this.api.obtener(id).pipe(
      catchError(err => {
        this.mostrarError(err);
        return of(null);
      })
    );
  }

  crear(request: CrearNotificacionInstitucionalRequest): Observable<NotificacionInstitucional | null> {
    this._guardando.set(true);
    this._errorEnlace.set(null);
    return this.api.crear(request).pipe(
      tap(() => {
        this._guardando.set(false);
        this.cargar();
        this.mostrarExito('Notificación creada', 'La notificación se guardó correctamente.');
      }),
      catchError(err => {
        this._guardando.set(false);
        this.mostrarErrorGuardado(err);
        return of(null);
      })
    );
  }

  editar(id: number, request: EditarNotificacionInstitucionalRequest): Observable<NotificacionInstitucional | null> {
    this._guardando.set(true);
    this._errorEnlace.set(null);
    return this.api.editar(id, request).pipe(
      tap(() => {
        this._guardando.set(false);
        this.cargar();
        this.mostrarExito('Notificación actualizada', 'Los cambios se guardaron correctamente.');
      }),
      catchError(err => {
        this._guardando.set(false);
        this.mostrarErrorGuardado(err);
        return of(null);
      })
    );
  }

  limpiarErrorEnlace(): void {
    this._errorEnlace.set(null);
  }

  // Si el 400 es de un campo del enlace, va inline a ese campo; cualquier otro
  // error (incluido el 400 de formato del DTO por tamaño, que trae otro texto)
  // sigue por el Swal de siempre.
  private mostrarErrorGuardado(err: { status?: number; error?: { mensaje?: string } }): void {
    const mensaje = err.error?.mensaje;
    const campo = err.status === 400 ? campoDeErrorEnlace(mensaje) : null;
    if (campo && mensaje) {
      this._errorEnlace.set({ campo, mensaje });
      return;
    }
    this.mostrarError(err);
  }

  eliminar(id: number): Observable<boolean> {
    this._guardando.set(true);
    return this.api.eliminar(id).pipe(
      tap(() => {
        this._guardando.set(false);
        this.cargar();
        this.mostrarExito('Notificación eliminada', 'La notificación se eliminó correctamente.');
      }),
      switchMap(() => of(true)),
      catchError(err => {
        this._guardando.set(false);
        this.mostrarError(err);
        return of(false);
      })
    );
  }

  private mostrarError(err: { status?: number; error?: { mensaje?: string } }): void {
    // 401: el authInterceptor ya hace logout() + navigate(['/login']) — no
    // duplicar con un popup técnico encima de esa redirección.
    if (err.status === 401) return;
    Swal.fire({
      title: 'Error',
      text: err.error?.mensaje ?? mensajePorDefecto(err.status),
      icon: 'error', confirmButtonText: 'Aceptar', confirmButtonColor: '#d33'
    });
  }

  mostrarExito(titulo: string, mensaje: string): void {
    Swal.fire({ title: titulo, text: mensaje, icon: 'success', confirmButtonText: 'Aceptar', confirmButtonColor: '#3085d6' });
  }

  async confirmarAccion(mensaje: string, titulo = '¿Estás seguro?'): Promise<boolean> {
    const result = await Swal.fire({
      title: titulo, text: mensaje, icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Sí, continuar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6', cancelButtonColor: '#d33'
    });
    return result.isConfirmed;
  }
}
